import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Send, VolumeX, EyeOff, Trash2, Loader2, Users, MapPin, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

// SafeBlur — local heuristic wordlist (runs on-device; chats stay private).
// Word-boundary matched to avoid false positives on short words.
const HARMFUL_WORDS = [
  // en
  "fuck", "shit", "bitch", "cunt", "whore", "slut", "nigger", "faggot",
  "rape", "rapist", "kys", "kill yourself", "nazi", "pedo", "pedophile",
  // sv
  "fitta", "kuk", "hora", "luder", "knulla", "jävla hora", "neger",
  "våldta", "våldtäkt", "ta livet av dig", "cp-barn", "mongo",
];
const HARMFUL_RE = new RegExp(`(^|[^\\p{L}])(${HARMFUL_WORDS.join("|").replace(/ /g, "\\s+")})($|[^\\p{L}])`, "iu");

export default function ChatDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [chat, setChat] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [revealed, setRevealed] = useState(() => new Set());
  const scrollRef = useRef(null);

  const [showMembers, setShowMembers] = useState(false);
  const [memberUsers, setMemberUsers] = useState([]);

  const mutedWords = user?.muted_words || [];
  const safeBlur = user?.safe_blur !== false;
  const isMuted = (txt) => {
    const low = (txt || "").toLowerCase();
    return mutedWords.some((w) => low.includes(w));
  };
  const isHarmful = (txt) => HARMFUL_RE.test(txt || "");
  const reveal = (mid) => setRevealed((s) => new Set(s).add(mid));

  const [busyDelete, setBusyDelete] = useState(false);

  const isModerator = user?.role === "admin" && (user?.is_master || user?.email === "mustafahaidari03@gmail.com" || (user?.admin_permissions || []).includes("reports") || (user?.admin_permissions || []).includes("users"));
  const canDelete = isModerator || chat?.participant_ids?.includes(user?.id);

  const deleteConversation = async () => {
    if (!window.confirm("Are you sure you want to permanently delete this entire chat conversation and all messages? This action cannot be undone.")) return;
    setBusyDelete(true);
    try {
      await api.delete(`/chats/${id}`);
      toast.success("Conversation deleted successfully");
      nav("/chats", { replace: true });
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyDelete(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/chats/${id}/messages`);
      setChat(data.chat); setMsgs(data.messages);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);
    } catch (e) { /* silently handle load errors */ }
  }, [id]);
  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [load]);

  /* ── Fetch member profiles when modal opens ──────────────── */
  useEffect(() => {
    if (!showMembers || !chat?.participant_ids?.length) return;
    let alive = true;
    Promise.all(
      chat.participant_ids.map((uid) =>
        api.get(`/users/${uid}`).then((r) => r.data).catch(() => null)
      )
    ).then((arr) => {
      if (alive) setMemberUsers(arr.filter(Boolean));
    });
    return () => { alive = false; };
  }, [showMembers, chat?.participant_ids?.join(",")]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const tx = text;
    setText("");
    try { await api.post(`/chats/${id}/messages`, { text: tx }); load(); } catch (e) { console.warn("Failed to send message:", e); }
  };

  return (
    <div className="max-w-2xl mx-auto h-screen flex flex-col">
      <div className="px-4 sm:px-6 pt-4 pb-3 border-b-2 border-[#121212] bg-white sticky top-0 flex items-center gap-3 z-10">
        <button onClick={() => nav(-1)} className="btn-nb !p-2" data-testid="back-btn"><ArrowLeft className="w-4 h-4" strokeWidth={3}/></button>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black truncate">{chat?.title || t("chatDetail.chat")}</div>
          <div className="text-xs text-[#4A4A4A]">{chat?.participant_ids?.length || 0} {t("chatDetail.members")}</div>
        </div>
        {/* Members button */}
        <button
          onClick={() => setShowMembers(true)}
          className="btn-nb !p-2"
          data-testid="members-btn"
          aria-label="Members"
        >
          <Users className="w-4 h-4" strokeWidth={2.5} />
        </button>
        {/* Live map button — only for activity-linked chats */}
        {chat?.activity_id && (
          <button
            onClick={() => nav(`/activity/${chat.activity_id}/map`)}
            className="btn-nb !p-2"
            data-testid="live-map-btn"
            aria-label="Live map"
          >
            <MapPin className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
        {canDelete && (
          <button
            onClick={deleteConversation}
            disabled={busyDelete}
            className="btn-nb btn-nb-coral !p-2 flex items-center justify-center shrink-0"
            data-testid="delete-chat-btn"
            aria-label="Delete Chat"
          >
            {busyDelete ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" strokeWidth={2.5} />
            )}
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FFFDF7]" data-testid="messages-list">
        {msgs.length === 0 && <p className="text-center text-[#7A7A7A] mt-10">{t("chatDetail.noMessages")}</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          const shown = revealed.has(m.id);
          const muted = !mine && !shown && isMuted(m.text);
          const blurred = !mine && !shown && !muted && safeBlur && isHarmful(m.text);
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] p-3 border-2 border-[#121212] rounded-2xl ${mine ? "bg-[#FF6B6B] text-white shadow-nb-sm rounded-br-md" : "bg-white shadow-nb-sm rounded-bl-md"}`} data-testid={`msg-${m.id}`}>
                {!mine && (
                  <Link to={`/u/${m.sender_id}`} className="block text-[10px] font-display font-black mb-0.5 opacity-70 hover:opacity-100 transition-opacity">
                    {m.sender_name}
                  </Link>
                )}
                {muted ? (
                  <button onClick={() => reveal(m.id)} className="flex items-center gap-1.5 text-sm text-[#7A7A7A] italic" data-testid={`msg-muted-${m.id}`}>
                    <VolumeX className="w-4 h-4" /> {t("chatDetail.hiddenMuted")}
                  </button>
                ) : blurred ? (
                  <button onClick={() => reveal(m.id)} className="text-left" data-testid={`msg-blurred-${m.id}`}>
                    <span className="whitespace-pre-wrap break-words select-none" style={{ filter: "blur(6px)" }} aria-hidden="true">{m.text}</span>
                    <span className="flex items-center gap-1 text-[11px] text-[#7A7A7A] mt-1 font-bold not-italic">
                      <EyeOff className="w-3.5 h-3.5" /> {t("chatDetail.tapToReveal")}
                    </span>
                  </button>
                ) : (
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                )}
                <div className={`text-[10px] mt-1 ${mine ? "opacity-80" : "text-[#7A7A7A]"}`}>{new Date(m.created_at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}</div>
              </div>
            </div>
          );
        })}
      </div>

      <form
        onSubmit={send}
        className="p-3 border-t-2 border-[#121212] bg-white flex gap-2 sticky bottom-0 z-20"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <input className="input-nb" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("chatDetail.typeMessage")} data-testid="msg-input"/>
        <button type="submit" className="btn-nb btn-nb-primary !px-4" data-testid="send-btn"><Send className="w-4 h-4" strokeWidth={3}/></button>
      </form>

      {/* ── Members modal ─────────────────────────────────────── */}
      {showMembers && (
        <div className="fixed inset-0 bg-[#121212]/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowMembers(false)}>
          <div className="card-nb p-6 max-w-md w-full bg-white animate-pop" onClick={(e) => e.stopPropagation()} data-testid="members-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-xl">Members</h3>
              <button onClick={() => setShowMembers(false)} className="btn-nb !p-2" data-testid="members-modal-close">
                <Plus className="w-4 h-4 rotate-45" />
              </button>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {memberUsers.length === 0 && (
                <p className="text-center text-[#7A7A7A] py-4">Loading…</p>
              )}
              {memberUsers.map((p) => (
                <Link
                  key={p.id}
                  to={`/u/${p.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F1EA] transition-colors"
                  onClick={() => setShowMembers(false)}
                  data-testid={`member-${p.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#F4F1EA] border-2 border-[#121212] flex items-center justify-center font-display font-black overflow-hidden shrink-0">
                    {p.avatar_url
                      ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
                      : (p.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{p.name}</div>
                  </div>
                  {p.id === user?.id && (
                    <span className="text-[10px] tag-nb bg-[#00B4D8] text-white !py-0">You</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
